// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
// @ts-ignore
function lazyLoadView(AsyncView) {
    const AsyncHandler = () => ({
        component: AsyncView,
        // A component to use while the component is loading.
        // loading: require('@views/_loading').default, // TODO
        // Delay before showing the loading component.
        // Default: 200 (milliseconds).
        delay: 400,
        // A fallback component in case the timeout is exceeded
        // when loading the component.
        // error: require('@views/_timeout').default, // TODO
        // Time before giving up trying to load the component.
        // Default: Infinity (milliseconds).
        timeout: 10000,
    });

    return Promise.resolve({
        functional: true,
        // @ts-ignore
        render(h, { data, children }) {
            // Transparently pass any props or children
            // to the view component.
            return h(AsyncHandler, data, children);
        },
    });
}


export default [
    {
        path: '/tier',
        redirect: (to: object) => {
            console.log(to);
            return { name: 'signup', query: { referrer: to.query.id } };
        },
    },
    {
        path: '/login',
        name: 'login',
        component: () => lazyLoadView(import('@/views/Login/Login.vue')),
    },
    {
        path: '/signup',
        name: 'signup',
        component: () => lazyLoadView(import('@/views/Login/Signup.vue')),
    },
    {
        path: '/changePass',
        name: 'changePass',
        component: () => lazyLoadView(import('@/views/Login/PasswordReset.vue')),
    },
    {
        path: '/updatePassword',
        name: 'updatePassword',
        component: () => lazyLoadView(import('@/views/Login/UpdatePassword.vue')),
    },
    {
        path: '/home',
        name: 'home',
        meta: {
            authRequired: true,
        },
        component: () => lazyLoadView(import('@/components/PlayGround.vue')),
    },
    {
        path: '*',
        hidden: true,
        component: () => lazyLoadView(import('@/views/404.vue')),
    },
    {
        path: '/',
        component: () => lazyLoadView(import('@/components/layouts/Home.vue')),
        children: [
            {
                path: '/',
                component: () => lazyLoadView(import('@/views/Dashboard/Dashboard.vue')),
                name: 'dashboard',
                meta: {
                    authRequired: true,
                },
            },
        ],
    },
    {
        path: '/programs',
        name: 'programs',
        component: () => lazyLoadView(import('@/components/layouts/Home.vue')),
        children: [
            {
                path: '/programs/campaign',
                component: () => lazyLoadView(import('@/views/Programs/Campaign/Campaign.vue')),
                name: 'campaign',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/programs/advertisements',
                component: () => lazyLoadView(import('@/views/Programs/Advertisement/AdsWrapper.vue')),
                name: 'advertisement',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/programs/referral',
                component: () => lazyLoadView(import('@/views/Programs/ReferralLinks/ReferralLinks.vue')),
                name: 'referral',
                meta: {
                    authRequired: true,
                },
            },
        ],
    },
    {
        path: '/reports',
        name: 'reports',
        component: () => lazyLoadView(import('@/components/layouts/Home.vue')),
        children: [
            {
                path: '/reports/achievement',
                component: () => lazyLoadView(import('@/views/Reports/Achievement/AchievementReport.vue')),
                name: 'achievement',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/reports/media',
                component: () => lazyLoadView(import('@/views/Reports/MediaTraffic/MediaTraffic.vue')),
                name: 'media',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/reports/financial',
                component: () => lazyLoadView(import('@/views/Reports/Financial/FinancialReport.vue')),
                name: 'financial',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/reports/tier',
                component: () => lazyLoadView(import('@/views/Reports/SubTierReport/SubTierReport.vue')),
                name: 'tierreport',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/reports/players',
                component: () => lazyLoadView(import('@/views/Reports/Players/Players.vue')),
                name: 'players',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/reports/rewards',
                component: () => lazyLoadView(import('@/views/Reports/Reward Plans/RewardPlans.vue')),
                name: 'rewards',
                meta: {
                    authRequired: true,
                },
            },
            {
                path: '/reports/transactions',
                component: () => lazyLoadView(import('@/views/Reports/Transactions/Transactions.vue')),
                name: 'transactions',
                meta: {
                    authRequired: true,
                },
            },
        ],
    },
    {
        path: '/introduction',
        name: 'introduction',
        component: () => lazyLoadView(import('@/components/layouts/Home.vue')),
        children: [
            {
                path: '/introduction/beginners',
                component: () => lazyLoadView(import('@/views/Introduction/Introduction.vue')),
                name: 'beginners',
                meta: {
                    authRequired: true,
                },
            },
        ],
    },


    {
        path: '/suspended',
        hidden: true,
        component: () => lazyLoadView(import('@/views/SuspendedTraffic.vue')),
    },
    {
        path: '/profile',
        name: 'profile',
        component: () => lazyLoadView(import('@/components/layouts/Home.vue')),
        children: [
            {
                path: '/profile/updateinfo',
                component: () => lazyLoadView(import('@/views/UpdateInformation.vue')),
                name: 'updateinfo',
                meta: {
                    authRequired: true,
                },
            },
        ],
    },
];
